import React from 'react';
import FdModal from '../FdModal';

const HIDDEN_COMMISSION_ROLES = ['AAH', 'AH', 'SAH', 'CEO'];

function formatCurrency(value) {
  if (value === null || value === undefined) {
    return '-';
  }

  return `PHP ${Number(value).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function valueOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

function roundAmount(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

export default function SaleDetailModal({ isOpen, onClose, sale, collections = null }) {
  const visibleCommissionAgents = Array.isArray(sale?.commission_agents)
    ? sale.commission_agents.filter(
      (item) => !HIDDEN_COMMISSION_ROLES.includes(String(item?.role || '').toUpperCase())
    )
    : [];

  // When opened from Collections, the actual Paid collections are provided so
  // the modal can show the amount actually paid and the remaining balance.
  const hasCollections = Array.isArray(collections);
  const paidBySchedule = {};
  let totalPaid = 0;
  if (hasCollections && sale) {
    collections.forEach((row) => {
      if (Number(row.sale_id || 0) !== Number(sale.id || 0)) {
        return;
      }
      if (String(row.status || '') !== 'Paid') {
        return;
      }

      const scheduleNo = Number(row.schedule_no || 0);
      const amount = Number(row.premium_amount || 0);
      paidBySchedule[scheduleNo] = (paidBySchedule[scheduleNo] || 0) + amount;
      totalPaid += amount;
    });
  }
  totalPaid = roundAmount(totalPaid);
  const remainingBalance = roundAmount(Math.max(0, Number(sale?.total_amount || 0) - totalPaid));

  return (
    <FdModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Sales"
      title={sale?.transaction_no ? `Sale ${sale.transaction_no}` : 'Sale Details'}
      subtitle="Review full sales transaction details."
      wide
      footer={(
        <button type="button" className="fd-btn fd-btn-cancel" onClick={onClose}>
          Close
        </button>
      )}
    >
      {sale ? (
        <div className="sale-view-content">
          <div className="sale-view-header-grid">
            <div>
              <p className="sale-view-label">Date</p>
              <p>{valueOrDash(sale.date)}</p>
            </div>
            <div>
              <p className="sale-view-label">Status</p>
              <span className={`status-badge status-${String(sale.status || '').toLowerCase()}`}>
                {valueOrDash(sale.status)}
              </span>
            </div>
            <div>
              <p className="sale-view-label">Branch</p>
              <p>{valueOrDash(sale.branch)}</p>
            </div>
            <div>
              <p className="sale-view-label">Product</p>
              <p>{valueOrDash(sale.product_name)}</p>
            </div>
          </div>

          <div>
            <p className="fd-section-title">Client & Contact</p>
            <div className="sale-view-kv-grid">
              <p><strong>Company / Client</strong><span>{valueOrDash(sale.company_client_name)}</span></p>
              <p><strong>Contact Person</strong><span>{valueOrDash(sale.contact_person)}</span></p>
              <p><strong>Position</strong><span>{valueOrDash(sale.contact_position)}</span></p>
              <p><strong>Email</strong><span>{valueOrDash(sale.contact_email)}</span></p>
              <p><strong>Contact Number</strong><span>{valueOrDash(sale.contact_number)}</span></p>
              <p><strong>Main Agent</strong><span>{valueOrDash(sale.agent_name)}</span></p>
            </div>
          </div>

          <div>
            <p className="fd-section-title">Plan Items</p>
            {Array.isArray(sale.plan_items) && sale.plan_items.length > 0 ? (
              <div className="sale-view-list">
                {sale.plan_items.map((item, index) => (
                  <div key={`plan-item-view-${index}`} className="sale-view-list-item">
                    <div>
                      <strong>{valueOrDash(item.plan_name)}</strong>
                      <span>Qty: {valueOrDash(item.qty)}</span>
                    </div>
                    <div>
                      <span>{formatCurrency(item.premium_amount)}</span>
                      <strong>{formatCurrency(item.total)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sale-view-empty">No plan items.</p>
            )}
          </div>

          <div>
            <p className="fd-section-title">Amounts</p>
            <div className="sale-view-kv-grid">
              <p><strong>Total Premium</strong><span>{formatCurrency(sale.premium_amount)}</span></p>
              <p><strong>NAF Unit Cost</strong><span>{formatCurrency(sale.naf?.unit_cost)}</span></p>
              <p><strong>NAF Qty</strong><span>{valueOrDash(sale.naf?.qty)}</span></p>
              <p><strong>NAF Total</strong><span>{formatCurrency(sale.naf?.total)}</span></p>
              <p><strong>Grand Total</strong><span>{formatCurrency(sale.total_amount)}</span></p>
              {hasCollections ? (
                <>
                  <p><strong>Total Paid</strong><span>{formatCurrency(totalPaid)}</span></p>
                  <p><strong>Remaining Balance</strong><span>{formatCurrency(remainingBalance)}</span></p>
                </>
              ) : null}
            </div>
          </div>

          <div>
            <p className="fd-section-title">Payment</p>
            <div className="sale-view-kv-grid">
              <p><strong>Mode of Payment</strong><span>{valueOrDash(sale.mode_of_payment)}</span></p>
              <p><strong>Payment Terms</strong><span>{valueOrDash(sale.payment_terms)}</span></p>
            </div>
          </div>

          <div>
            <p className="fd-section-title">Payment Schedules</p>
            {Array.isArray(sale.payment_schedules) && sale.payment_schedules.length > 0 ? (
              <div className="sale-view-list">
                {sale.payment_schedules.map((schedule, index) => {
                  const schedulePaid = hasCollections
                    ? roundAmount(paidBySchedule[Number(schedule.schedule_no || 0)] || 0)
                    : 0;
                  const scheduleBalance = roundAmount(Math.max(0, Number(schedule.amount_due || 0) - schedulePaid));
                  const isPartial = hasCollections && !schedule.is_paid && schedulePaid > 0;

                  return (
                    <div key={`schedule-view-${index}`} className="sale-view-list-item">
                      <div>
                        <strong>Schedule {valueOrDash(schedule.schedule_no)}</strong>
                        <span>Due: {valueOrDash(schedule.due_date)}</span>
                        {isPartial ? (
                          <span>Paid: {formatCurrency(schedulePaid)} &bull; Balance: {formatCurrency(scheduleBalance)}</span>
                        ) : null}
                      </div>
                      <div>
                        <span className={`status-badge status-${schedule.is_paid ? 'active' : (isPartial ? 'pending' : 'inactive')}`}>
                          {schedule.is_paid ? 'Paid' : (isPartial ? 'Partial' : 'Unpaid')}
                        </span>
                        <strong>{formatCurrency(schedule.amount_due)}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="sale-view-empty">No payment schedules.</p>
            )}
          </div>

          <div>
            <p className="fd-section-title">Commission Agents</p>
            {visibleCommissionAgents.length > 0 ? (
              <div className="sale-view-list">
                {visibleCommissionAgents.map((agent, index) => (
                  <div key={`commission-view-${index}`} className="sale-view-list-item">
                    <div>
                      <strong>{valueOrDash(agent.role)}</strong>
                      <span>{valueOrDash(agent.agent_name)}</span>
                    </div>
                    <div>
                      <span className={`status-badge status-${agent.autodeduct ? 'active' : 'inactive'}`}>
                        {agent.autodeduct ? 'Auto-deduct' : 'Manual'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sale-view-empty">No commission agents.</p>
            )}
          </div>

          {sale.notes ? (
            <div>
              <p className="fd-section-title">Notes</p>
              <p className="sale-view-notes">{sale.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </FdModal>
  );
}
